import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button, Form, Input, Table, Tooltip} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {StyledBudgetLineItemsTable} from './StyledBudgetLineItemsTable';
import IconWrapper from '../../../IconWrapper';
import {TrashIcon} from '../../../../icons';
import {objectHelpers, textHelpers} from '../../../../utils/helpers';

const tableId = 'budget-line-items-table';

const createEmptyItem = (index = 0) => ({ key: String(index + 1), name: '', quantity: '', cost_per_unit: '', total_cost: '' });

const roundToTwo = textHelpers.roundToTwo;

const BudgetLineItemsTable = ({
  budget,
  editable = true,
  onEdit,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const [items, setItems] = useState([]);
  // ref to hold the debounce timeout id for auto-submit
  const submitTimeoutRef = useRef(null);

  useEffect(() => {
    // ensure cost_per_unit and total_cost values are rounded to 2 decimals when initializing
    const budgetItems = (budget?.items || []).map((item, index) => ({
      key: String(index + 1),
      ...item,
      cost_per_unit: roundToTwo(item.cost_per_unit),
      total_cost: roundToTwo(item.total_cost),
    }));

    // if there are no budget items, ensure we have one empty row
    const itemsToSet = budgetItems.length > 0 ? budgetItems : [createEmptyItem(0)];

    setItems(itemsToSet);
  }, [budget?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
    };
  }, []);

  const handleSubmitValue = (data) => {
    let itemsToSubmit = data || items;

    // if there is nothing to submit, add one empty item and don't call onEdit
    if (!itemsToSubmit || itemsToSubmit.length === 0) {
      const empty = createEmptyItem(0);
      setItems([empty]);
      return;
    }

    // clear any pending auto-submit timeout when submitting explicitly
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }

    // ensure cost_per_unit and total_cost values are rounded before submission
    itemsToSubmit = itemsToSubmit.map(it => ({
      ...it,
      cost_per_unit: roundToTwo(it.cost_per_unit),
      total_cost: roundToTwo(it.total_cost),
    }));

    const allFilled = itemsToSubmit.length > 0 && itemsToSubmit.every(item =>
      item.name && item.quantity !== '' && item.cost_per_unit !== '' && item.total_cost !== ''
    );
    const limitAmount = objectHelpers.summarizeNumericProperty(itemsToSubmit, 'total_cost');
    if (allFilled) onEdit?.({ data: { items: itemsToSubmit, limit_amount: limitAmount } });
  };

  const handleChangeValue = (e) => {
    const { target: { name, value, dataset: { key } } = {} } = e || {};

    const updatedItems = items.map((item) => {
      if (item.key === key) {
        // allow free typing for cost_per_unit (do not round here)
        let updatedItem = { ...item, [name]: value };
        if (name === 'quantity' || name === 'cost_per_unit') {
          // calculate numeric total and round to 2 decimals; handle string inputs
          const q = Number(updatedItem.quantity);
          const cpu = Number(updatedItem.cost_per_unit);
          const product = (!Number.isNaN(q) && !Number.isNaN(cpu)) ? q * cpu : updatedItem.total_cost;
          updatedItem = { ...updatedItem, total_cost: roundToTwo(product) };
        }
        return updatedItem;
      }
      return item;
    });

    setItems(updatedItems);

    // debounce submit: clear previous timeout and schedule a new one
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    submitTimeoutRef.current = setTimeout(() => {
      submitTimeoutRef.current = null;
      handleSubmitValue(updatedItems);
    }, 2000);
  };

  // onBlur: round cost_per_unit when user finishes editing the field
  const handleBlur = (e) => {
    const { target: { name, value, dataset: { key } } = {} } = e || {};
    if (!key || name !== 'cost_per_unit') return;

    const updatedItems = items.map(item => {
      if (item.key !== key) return item;
      const roundedCpu = roundToTwo(value);
      const q = Number(item.quantity);
      const cpuNum = Number(roundedCpu);
      const product = (!Number.isNaN(q) && !Number.isNaN(cpuNum)) ? q * cpuNum : item.total_cost;
      return { ...item, cost_per_unit: roundedCpu, total_cost: roundToTwo(product) };
    });

    setItems(updatedItems);
    // submit after blur
    handleSubmitValue(updatedItems);
  }

  const handleDeleteItem = (key) => {
    const updatedItems = items.filter(item => item.key !== key);

    setItems(updatedItems.length > 0 ? updatedItems : [createEmptyItem(0)]);
    const limitAmount = objectHelpers.summarizeNumericProperty(updatedItems, 'total_cost');
    onEdit?.({ data: { items: updatedItems, limit_amount: limitAmount } });
  }

  const formElementProps = {
    onChange: handleChangeValue,
    onBlur: handleBlur,
  }

  const getInputField = ({key, name, type = 'text', value, ...rest} = {}) => {
    if (!editable) return value;

    let inputProps = {
      'data-key': key,
      name,
      value,
      ...formElementProps,
      ...rest
    }

    if (type === 'number') {
      inputProps = { ...inputProps, type: 'number', min: 0.01 }
    }

    return (
      <Form.Item>
        <Input
          type={type}
          {...formElementProps}
          {...inputProps}
        />
      </Form.Item>
    )
  };

  const columns = [
    {
      dataIndex: 'name',
      title: t('name'),
      render: (value, { key }) => getInputField({
        key, name: 'name', value, placeholder: `${t('add')} ${t('name')}`
      }),
    },
    {
      dataIndex: 'cost_per_unit',
      title: t('costPerUnit'),
      render: (value, { key }) => getInputField({
        key, name: 'cost_per_unit', value, placeholder: `${t('add')} ${t('cost')}`, type: 'number'
      })
    },
    {
      dataIndex: 'quantity',
      title: t('quantity'),
      render: (value, { key }) => getInputField({
        key, name: 'quantity', value, placeholder: `${t('add')} ${t('quantity')}`, type: 'number'
      })
    },
    {
      dataIndex: 'total_cost',
      title: t('totalCost'),
      render: (value, { key }) => getInputField({
        key, name: 'total_cost', value, type: 'number', disabled: true
      })
    },
    editable && {
      dataIndex: 'actions',
      render: (_, { key }) => (
        <Tooltip title={t('delete')}>
          <Button
            icon={<IconWrapper size={16}><TrashIcon /></IconWrapper>}
            onClick={() => handleDeleteItem(key)}
            type='text'
          />
        </Tooltip>
      ),
    }
  ].filter(Boolean);

  const handleOnAddClick = () => {
    const newItem = createEmptyItem(items.length);
    setItems([...items, newItem]);
  }

  return (
    <StyledBudgetLineItemsTable size='large' direction='vertical' {...rest}>
      <Table id={tableId} dataSource={items} columns={columns} pagination={false} />
      {editable && (
        <Button
          icon={<PlusOutlined />}
          onClick={handleOnAddClick}
          type='link'
        >
          {t('addNewLine')}
        </Button>
      )}
    </StyledBudgetLineItemsTable>
  );
}

BudgetLineItemsTable.propTypes = {
  budget: PropTypes.object,
  editable: PropTypes.bool,
}

export default BudgetLineItemsTable;
